"use client";

import { useMemo, useState } from "react";
import { ErrorNotice, TextField } from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  EXPRESSION_ERROR_STRINGS,
  expressionErrorMessage,
} from "@/components/tools/calculus/expression-errors";
import { IntegralResults } from "@/components/tools/calculus/integral-results";
import {
  type Limit,
  readLimit,
} from "@/components/tools/calculus/read-constant";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  type Node,
  substitute,
  variable as variableNode,
} from "@/lib/tool-kit/calculus/ast";
import { compile } from "@/lib/tool-kit/calculus/evaluate";
import {
  type IntegralReport,
  integralReport,
} from "@/lib/tool-kit/calculus/integral-report";
import { type ExprError, parseExpression } from "@/lib/tool-kit/calculus/parse";

/** English strings. A translated content file overrides these through `ui`. */
export const DEFINITE_INTEGRAL_CALCULATOR_STRINGS = {
  functionLabel: "Function f(x)",
  functionHint:
    "Use + − * / ^ and brackets. Functions: sin, cos, tan, sec, csc, cot, asin, acos, atan, sinh, cosh, tanh, exp, ln, log (base 10), sqrt, abs. Constants: pi and e.",
  lowerLabel: "Lower limit a",
  upperLabel: "Upper limit b",
  limitHint:
    "A number or constant such as 0, −2 or pi/2. Type inf or −inf for an infinite limit.",
  results: "Results",
  valueLabel: "Integral value",
  errorLabel: "Estimated absolute error",
  totalLabel: "Total area ∫|f(x)|dx",
  evaluationsLabel: "Function evaluations",
  noValue: "no finite value",
  summaryHeading: "The integral",
  copyValue: "Copy value",
  copied: "Copied!",
  methodHeading: "Method",
  methodGaussKronrod:
    "Adaptive Gauss–Kronrod: a 7-point Gauss and a 15-point Kronrod rule on {intervals} subintervals; their difference estimates the error.",
  methodTanhSinh:
    "Double-exponential (tanh–sinh) quadrature, used because the integrand is not finite at an endpoint. Accurate to about eight digits.",
  signNote:
    "f changes sign on this interval, so the integral is net signed area (blue above the axis minus orange below); the total unsigned area is shown separately.",
  warnings: {
    "undefined-region":
      "f has no real value near x = {at} inside the interval (for example the square root or logarithm of a negative number), so the integral over this interval is not defined.",
    "singular-endpoint":
      "f is not finite at the endpoint x = {at}, so this is an improper integral there.",
    "singular-interior":
      "f is not finite at x = {at} inside the interval, so it was split there.",
    "infinite-limit":
      "Infinite limit: the substitution x = a + t/(1 − t) maps the range onto [0, 1).",
    "not-converged":
      "The method did not reach its tolerance; the trouble is near x ≈ {at}. Treat the value as approximate, using the error estimate, or split the interval at that point.",
    divergent:
      "The integral appears to diverge near x ≈ {at}: stopping closer and closer to that point keeps adding area without settling. No finite value is given.",
    "methods-disagree":
      "Two rules disagree (the other gave {other}); trust this value to only a few digits.",
  },
  graphHeading: "Graph",
  graphTitle:
    "Graph of y = f(x) with the area between the curve and the x-axis shaded from {lower} to {upper}",
  graphWindowNote:
    "The graph shows a window around the interval; the integral itself runs over the full range.",
  seriesFunction: "y = f(x)",
  seriesPositive: "Area above the axis",
  seriesNegative: "Area below the axis",
  downloadPng: "Download graph (PNG)",
  downloadSvg: "Download graph (SVG)",
  infinity: "∞",
  limitInvalid:
    "Enter each limit as a number or constant such as 0, −2 or pi/2, or inf / −inf.",
  oneVariable: "Use a function of one variable, such as x^2 sin(x).",
  enterLimits: "Enter both limits.",
  expressionErrors: EXPRESSION_ERROR_STRINGS,
} as const;

export type IntegralStrings = typeof DEFINITE_INTEGRAL_CALCULATOR_STRINGS;

export type IntegralView =
  | { kind: "parse-error"; error: ExprError }
  | { kind: "many-variables" }
  | { kind: "blank-limit" }
  | { kind: "bad-limit" }
  | {
      kind: "ok";
      ast: Node;
      f: (x: number) => number;
      lower: Limit;
      upper: Limit;
      decimals: boolean;
      report: IntegralReport;
    };

function buildView(
  text: string,
  lowerText: string,
  upperText: string
): IntegralView {
  const parsed = parseExpression(text);
  if (!parsed.ok) {
    return { kind: "parse-error", error: parsed.error };
  }
  if (parsed.variables.length > 1) {
    return { kind: "many-variables" };
  }
  const original = parsed.variables[0] ?? "x";
  const ast =
    original === "x"
      ? parsed.ast
      : substitute(parsed.ast, original, variableNode("x"));
  const lower = readLimit(lowerText);
  const upper = readLimit(upperText);
  if (lower === null || upper === null) {
    return { kind: "blank-limit" };
  }
  if (lower === "invalid" || upper === "invalid") {
    return { kind: "bad-limit" };
  }
  const f = compile(ast, "x");
  return {
    kind: "ok",
    ast,
    f,
    lower,
    upper,
    decimals: parsed.usesDecimals,
    report: integralReport(f, lower.value, upper.value),
  };
}

function problemMessage(
  view: IntegralView,
  strings: IntegralStrings
): string | null {
  switch (view.kind) {
    case "parse-error":
      return expressionErrorMessage(view.error, strings.expressionErrors);
    case "many-variables":
      return strings.oneVariable;
    case "blank-limit":
      return strings.enterLimits;
    case "bad-limit":
      return strings.limitInvalid;
    default:
      return null;
  }
}

export default function DefiniteIntegralCalculatorWidget() {
  const strings = useToolUi(DEFINITE_INTEGRAL_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("sin(x)");
  const [lowerText, setLowerText] = useState("0");
  const [upperText, setUpperText] = useState("pi");
  const view = useMemo(
    () => buildView(text, lowerText, upperText),
    [text, lowerText, upperText]
  );
  const message = problemMessage(view, strings);
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <TextField
            hint={strings.functionHint}
            id="di-function"
            label={strings.functionLabel}
            mono
            onChange={setText}
            value={text}
          />
        </div>
        <TextField
          id="di-lower"
          label={strings.lowerLabel}
          mono
          onChange={setLowerText}
          value={lowerText}
        />
        <TextField
          hint={strings.limitHint}
          id="di-upper"
          label={strings.upperLabel}
          mono
          onChange={setUpperText}
          value={upperText}
        />
      </div>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {message ? <ErrorNotice>{message}</ErrorNotice> : null}
        {view.kind === "ok" ? (
          <IntegralResults fmt={fmt} strings={strings} view={view} />
        ) : null}
      </section>
    </>
  );
}
